import React, { useCallback, useMemo } from 'react';
import {
  FloppyIcon,
  PencilIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import { useNaurEventsForBuilding } from '../../../hooks/data-fetching/useNaurEvents';
import v1Messages from '../../ManageBuildings/messages';
import appMessages from '../../App/messages';
import messages from '../messages';
import {
  buildUpdatePayload,
  fractionsChanged,
  hasUnresolvedNaurs,
  toFormValues,
} from '../utils';
import { buildFields } from './fields';
import type { Building } from '../../ManageBuildings/types';
import type { Gates } from '../types';

export type EditBuildingModalProps = {
  open: boolean,
  building: Building,
  states: { id: string, code: string }[],
  gates: Gates,
  intl: Object,
  constructionTypeOptions: { value: string, text: string }[],
  handleClose: Function,
  openConfirm: boolean,
  openConfirmModal: Function,
  closeConfirmModal: Function,
  updateBuilding: { mutate: Function, isLoading: boolean },
  promptToaster: Function,
};

const useEditBuildingModal = ({
  open,
  building,
  states,
  gates,
  intl,
  constructionTypeOptions,
  handleClose,
  openConfirmModal,
  closeConfirmModal,
  updateBuilding,
  promptToaster,
}: EditBuildingModalProps) => {
  const defaultValues = useMemo(() => toFormValues(building), [building]);
  const ReactHookFormProps = useReactHookFormProps({
    defaultValues,
    mode: 'onBlur',
  });
  const { handleSubmit, isDirty, watch } = ReactHookFormProps;
  const stateValue = watch('state');
  const cityValue = watch('city');

  const naurEnabled = Boolean(open && building?.id && gates.showFractions);
  const {
    data: naurEventsData,
    isLoading: isNaurLoading,
    isError: isNaurError,
  } = useNaurEventsForBuilding(building?.id, naurEnabled);

  const unresolvedNaurs = hasUnresolvedNaurs({
    naur: gates.showFractions,
    isLoading: isNaurLoading,
    isError: isNaurError,
    naurEvents: naurEventsData?.naurEvents,
  });

  const fields = useMemo(
    () =>
      buildFields({
        gates,
        states,
        intl,
        stateValue,
        cityValue,
        unresolvedNaurs,
        isNaurLoading,
        isNaurError,
        constructionTypeOptions,
      }),
    [
      gates,
      states,
      intl,
      stateValue,
      cityValue,
      unresolvedNaurs,
      isNaurLoading,
      isNaurError,
      constructionTypeOptions,
    ],
  );

  const isSaving = updateBuilding.isLoading;

  const onValid = useCallback(
    (values) => {
      if (isSaving) return;

      const payload = buildUpdatePayload({ values, building, gates });
      const changed = fractionsChanged(building, payload);

      if (changed && unresolvedNaurs) {
        promptToaster({
          type: 'error',
          title: intl.formatMessage(appMessages.error),
          message: intl.formatMessage(v1Messages.errorUnresolvedNaursOnSave),
        });
        return;
      }

      updateBuilding.mutate(
        { id: building.id, payload, fractionsChanged: changed },
        { onSuccess: handleClose },
      );
    },
    [
      isSaving,
      building,
      gates,
      unresolvedNaurs,
      promptToaster,
      intl,
      updateBuilding,
      handleClose,
    ],
  );

  const handleCancel = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      openConfirmModal();
    } else {
      handleClose();
    }
  }, [isSaving, isDirty, openConfirmModal, handleClose]);

  const discardAndClose = useCallback(() => {
    closeConfirmModal();
    handleClose();
  }, [closeConfirmModal, handleClose]);

  const modalProps = useMemo(
    () => ({
      title: `${intl.formatMessage(messages.editBuilding)} ${building.name}`,
      icon: <PencilIcon />,
      ModalProps: { maxWidth: 'md', fullWidth: true },
      actionsProps: [
        {
          children: intl.formatMessage(appMessages.cancel),
          onClick: handleCancel,
          disabled: isSaving,
        },
        {
          children: intl.formatMessage(messages.saveChanges),
          startIcon: <FloppyIcon />,
          submitButton: true,
          isSubmitting: isSaving,
          disabled: !isDirty || isSaving,
          onClick: handleSubmit(onValid),
        },
      ],
    }),
    [
      intl,
      building.name,
      handleCancel,
      isSaving,
      isDirty,
      handleSubmit,
      onValid,
    ],
  );

  const confirmModalProps = useMemo(
    () => ({
      title: intl.formatMessage(messages.discardChangesTitle),
      children: intl.formatMessage(messages.discardChangesBody),
      onClose: closeConfirmModal,
      actionsProps: [
        {
          children: intl.formatMessage(appMessages.no),
          onClick: closeConfirmModal,
        },
        {
          children: intl.formatMessage(appMessages.yes),
          onClick: discardAndClose,
        },
      ],
    }),
    [intl, closeConfirmModal, discardAndClose],
  );

  return {
    ReactHookFormProps,
    fields,
    modalProps,
    confirmModalProps,
    handleCancel,
    unresolvedNaurs,
  };
};

export default useEditBuildingModal;
